import Divider from '@shared/components/Divider/Divider';
import InputGroup from '@shared/components/InputGroup/InputGroup';
import Popup from '@shared/components/Popup/Popup';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import ModalFooter from 'components/ModalFooter';
import useCreateWorkspaceRole from 'modules/Workspaces/hooks/useCreateWorkspaceRole/useCreateWorkspaceRole';

import PermissionCategories from './PermissionCategories';
import useWorkspaceRolesForm, { WorkspaceRolesFormType } from './useWorkspaceRolesForm';
import WorkspaceFormField from '../WorkspaceCreateStep/WorkspaceFormField';

interface WorkspaceRoleCreateProps {
  onGoBack: () => void;
  workspaceId: string | null;
}

const WorkspaceRoleCreate = ({ onGoBack, workspaceId }: WorkspaceRoleCreateProps) => {
  const { t } = useTranslation();
  const { createWorkspaceRole, isLoading } = useCreateWorkspaceRole();

  const form = useWorkspaceRolesForm();
  const {
    formState: { errors, isValid },
    handleSubmit: onSubmitForm,
    register,
  } = form;

  const handleSubmit = ({ roleName, permissions }: WorkspaceRolesFormType) =>
    createWorkspaceRole({ workspaceId, name: roleName.trim(), permissions }).then(onGoBack);

  return (
    <FormProvider {...form}>
      <Popup isVisible closePopup={onGoBack} wrapperId="create-workspace-modal">
        <Popup.FullScreenContainer className="md:w-[658px] md:max-w-[658px]">
          <div className="px-4">
            <Popup.Header className="shadow-none border-none !mx-0">
              <span className="text-size-md font-medium">{t('workspaces.create_role')}</span>
              <Popup.CloseIcon closePopup={onGoBack} className="!right-0" />
            </Popup.Header>
            <Divider className="hidden md:block" />
          </div>
          <form onSubmit={onSubmitForm(handleSubmit)} className="flex flex-col grow overflow-y-auto gap-4 px-4 md:pt-4">
            <InputGroup label={t('workspaces.name_role')} errorMessage={errors.roleName?.message}>
              <InputGroup.Input
                {...register('roleName')}
                placeholder={t('workspaces.role_placeholder')}
                className="border-primary-50"
                hasError={!!errors.roleName?.message}
                autoComplete="off"
              />
            </InputGroup>
            <WorkspaceFormField label={t('workspaces.role_permissions')}>
              <PermissionCategories className="mb-4" isEditOrCreate />
            </WorkspaceFormField>
          </form>
          <ModalFooter
            onCloseClick={onGoBack}
            onSubmit={onSubmitForm(handleSubmit)}
            successButtonText={t('save')}
            cancelText={t('cancel')}
            successDisabled={!isValid || isLoading}
            isLoading={isLoading}
          />
        </Popup.FullScreenContainer>
      </Popup>
    </FormProvider>
  );
};

export default WorkspaceRoleCreate;
